import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { patientPortal } from '@/api/patientPortal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { usePatientPortalBranding } from './PatientPortalBrandingProvider';

type Step = 'mobile' | 'profile' | 'otp' | 'newPassword' | 'done';

type PatientPick = { id: string; name: string; uhid?: string | null };

export default function PatientPortalForgotPassword() {
  const navigate = useNavigate();
  const branding = usePatientPortalBranding();
  const [step, setStep] = useState<Step>('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [patients, setPatients] = useState<PatientPick[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const selectedPatient = patients.find((p) => p.id === selectedPatientId) ?? null;

  const sendOtp = async (patientId?: string) => {
    setLoading(true);
    setDevOtp(null);
    try {
      const res = await patientPortal.forgotPasswordOtpSend({
        identifier: mobile.trim(),
        patientId,
      });
      if (res.requiresPatientSelection && res.patients?.length) {
        setPatients(res.patients);
        setStep('profile');
        return;
      }
      const resolvedPatientId = patientId ?? res.patientId;
      if (resolvedPatientId) {
        setSelectedPatientId(resolvedPatientId);
      }
      if (patients.length === 0) {
        try {
          const lookup = await patientPortal.lookupAccounts(mobile.trim());
          const list = (lookup.patients ?? []) as PatientPick[];
          if (list.length > 0) setPatients(list);
        } catch {
          // optional — verify still works with patientId alone
        }
      }
      setStep('otp');
      if (res.devOtp) setDevOtp(res.devOtp);
      toast.success('OTP sent on WhatsApp', {
        description:
          'Open WhatsApp from T.R.U.E. Hospitals (+91 70117 43241). Check Updates / verification messages.',
        duration: 12000,
      });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    setLoading(true);
    try {
      const res = await patientPortal.forgotPasswordOtpVerify(selectedPatientId, otp);
      setResetToken(res.resetToken);
      setStep('newPassword');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!resetToken) {
      toast.error('Session expired. Start again.');
      return;
    }
    setLoading(true);
    try {
      await patientPortal.resetPassword(resetToken, newPassword);
      setStep('done');
      toast.success('Password updated. You can sign in now.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 p-8 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Reset password</h1>
          {branding.hospitalName ? (
            <p className="text-sm font-medium text-slate-700 mt-1">{branding.hospitalName}</p>
          ) : null}
          <p className="text-sm text-muted-foreground mt-1">
            {step === 'mobile' && 'We will send an OTP to your registered mobile on WhatsApp.'}
            {step === 'profile' && 'Choose your profile'}
            {step === 'otp' && 'Enter the OTP from WhatsApp'}
            {step === 'newPassword' && 'Choose a new password'}
            {step === 'done' && 'Your password has been reset.'}
          </p>
        </div>

        {step === 'mobile' && (
          <>
            <div>
              <Label>Registered mobile number</Label>
              <Input
                className="mt-1 h-11"
                placeholder="9876543210"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>
            <Button
              className="w-full h-11 bg-teal-800 hover:bg-teal-900"
              onClick={() => sendOtp()}
              disabled={loading || mobile.trim().length < 10}
            >
              Send OTP
            </Button>
          </>
        )}

        {step === 'profile' && (
          <>
            <div className="space-y-2">
              {patients.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPatientId(p.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${
                    selectedPatientId === p.id
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-slate-200'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
            <Button
              className="w-full h-11 bg-teal-800 hover:bg-teal-900"
              onClick={() => sendOtp(selectedPatientId ?? undefined)}
              disabled={loading || !selectedPatientId}
            >
              Send OTP
            </Button>
          </>
        )}

        {step === 'otp' && (
          <>
            {patients.length > 1 && (
              <div className="space-y-2">
                <Label>Who is resetting password?</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {patients.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPatientId(p.id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${
                        selectedPatientId === p.id
                          ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      <span className="font-medium text-slate-900">{p.name}</span>
                      {p.uhid ? (
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          UHID {p.uhid}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedPatient && patients.length <= 1 && (
              <p className="text-sm text-slate-600">
                OTP sent for <strong>{selectedPatient.name}</strong>
                {selectedPatient.uhid ? ` · ${selectedPatient.uhid}` : ''}
              </p>
            )}
            <div>
              <Label>6-digit OTP</Label>
              <Input
                className="mt-1 h-11"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
              />
              {devOtp && <p className="text-amber-700 text-sm mt-1">Dev OTP: {devOtp}</p>}
            </div>
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => sendOtp(selectedPatientId ?? undefined)}
              disabled={loading}
            >
              Resend OTP
            </Button>
            <Button
              className="w-full h-11 bg-teal-800 hover:bg-teal-900"
              onClick={verifyOtp}
              disabled={loading || otp.length < 4 || !selectedPatientId}
            >
              Verify OTP
            </Button>
          </>
        )}

        {step === 'newPassword' && (
          <>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                className="mt-1 h-11"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input
                type="password"
                className="mt-1 h-11"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <Button
              className="w-full h-11 bg-teal-800 hover:bg-teal-900"
              onClick={savePassword}
              disabled={loading}
            >
              Update password
            </Button>
          </>
        )}

        {step === 'done' && (
          <Button
            className="w-full h-11 bg-teal-800 hover:bg-teal-900"
            onClick={() => navigate('/patient-loyalty/login')}
          >
            Go to sign in
          </Button>
        )}

        {step !== 'done' && (
          <p className="text-center text-sm">
            <Link to="/patient-loyalty/login" className="text-teal-700 underline">
              Back to sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
